import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const COMMON_UNITS = ['pz', 'ora', 'kg', 'mq', 'mc', 'km', 'lt', 'forfait'];
const MWST_OPTIONS = [
  { value: '8.1', label: '8.1% (aliquota normale)' },
  { value: '2.6', label: '2.6% (aliquota ridotta)' },
  { value: '0', label: '0% (esente)' },
];

/**
 * Compact popup shown when the reviewer creates a free article from the combobox.
 * Asks for unit (required), IVA (optional — empty = warning at approve time).
 * Price is never asked here; the reviewer or accounting will fill it later.
 *
 * Props:
 *   open, onOpenChange
 *   initialName    — name typed in the combobox
 *   existingUnits  — unique strings from the catalog (for the dropdown)
 *   onConfirm({ name, unit, mwst_rate })  → parent persists via POST /lines.
 */
export function FreeArticleDialog({
  open, onOpenChange,
  initialName = '',
  existingUnits = [],
  onConfirm,
}) {
  const [name, setName] = useState(initialName);
  const [unit, setUnit] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [mwst, setMwst] = useState(''); // '' = not selected → warning

  useEffect(() => {
    if (open) {
      setName(initialName);
      setUnit('');
      setCustomUnit('');
      setMwst('');
    }
  }, [open, initialName]);

  // Build dedup list: catalog units + common ones, lowercased compare
  const unitOptions = Array.from(new Set([
    ...existingUnits.filter(Boolean).map(u => u.toString().trim()),
    ...COMMON_UNITS,
  ]));

  const isCustom = unit === '__custom__';
  const finalUnit = isCustom ? customUnit.trim() : unit;

  const canSubmit = name.trim().length > 0 && finalUnit.length > 0;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm?.({
      name: name.trim(),
      unit: finalUnit,
      mwst_rate: mwst === '' ? null : parseFloat(mwst),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="free-article-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo articolo libero</DialogTitle>
          <DialogDescription>
            L'articolo non è nel catalogo. Imposta l'unità di misura e l'aliquota IVA.
            Il prezzo potrà essere assegnato durante la revisione o la fatturazione.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="free-name">Descrizione *</Label>
            <Input
              id="free-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Trasporto materiale"
              data-testid="free-article-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="free-unit">Unità di misura *</Label>
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger id="free-unit" data-testid="free-article-unit">
                <SelectValue placeholder="Seleziona unità" />
              </SelectTrigger>
              <SelectContent>
                {unitOptions.map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
                <SelectItem value="__custom__">+ Nuova unità…</SelectItem>
              </SelectContent>
            </Select>
            {isCustom && (
              <Input
                value={customUnit}
                onChange={(e) => setCustomUnit(e.target.value)}
                placeholder="Es. tonnellata"
                className="mt-2"
                data-testid="free-article-custom-unit"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="free-mwst">Aliquota IVA</Label>
            <Select value={mwst} onValueChange={setMwst}>
              <SelectTrigger id="free-mwst" data-testid="free-article-mwst">
                <SelectValue placeholder="Lascia vuoto per decidere dopo" />
              </SelectTrigger>
              <SelectContent>
                {MWST_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">
              Se non viene impostata ora, sarà richiesta prima di emettere la fattura.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="free-article-cancel">
            Annulla
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit} data-testid="free-article-confirm">
            Aggiungi articolo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
